"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  type GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import Link from "next/link";
import type { ComponentProps } from "react";

const glassButtonVariants = cva(
  "block flex items-center justify-center size-12 rounded-full backdrop-blur-[20px] glass-border glass-border-hover active:scale-90 transition-transform duration-200",
  {
    variants: {
      variant: {
        icon: "[&>*]:hover:scale-[1.2] [&>*]:transition-all [&>*]:duration-200 bg-white/5",
        cta: "group relative overflow-hidden accent-border animated-border accent-background",
      },
    },
    defaultVariants: {
      variant: "icon",
    },
  },
);

type GlassButtonProps = {
  label?: string;
  subLabel?: string;
  labelsClassName?: string;
  control: string;
  section?: GamePageSection;
} & VariantProps<typeof glassButtonVariants> &
  (ComponentProps<"button"> | ComponentProps<typeof Link>);

export const GlassButton = ({
  label,
  subLabel,
  labelsClassName,
  control,
  section,
  variant,
  ...buttonProps
}: GlassButtonProps) => {
  const { track } = useGamePageAnalytics();
  const { children, className, onClick, ...rest } = buttonProps;

  const handleClick = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLAnchorElement>,
  ) => {
    track({
      action: Action.PRESSED,
      extras: {
        control: control.toLowerCase() as Lowercase<string>,
        controlType: "href" in rest ? ControlType.LINK : ControlType.BUTTON,
        section,
        controlLabel: label || subLabel || control,
      },
    });
    onClick?.(e as never);
  };

  const buttonClassName = cn(glassButtonVariants({ variant }), className);

  const content = (
    <>
      {children}
      {variant === "cta" && (
        <span aria-hidden className="duration-300 ease-in-out cta-glow" />
      )}
    </>
  );

  // Render the base element directly (not as a nested component)
  const isLink = "href" in rest;
  const baseElement = isLink ? (
    <Link
      className={buttonClassName}
      {...(rest as ComponentProps<typeof Link>)}
      onClick={handleClick}
    >
      {content}
    </Link>
  ) : (
    <button
      className={buttonClassName}
      {...(rest as ComponentProps<"button">)}
      onClick={handleClick}
    >
      {content}
    </button>
  );

  if (label === undefined && subLabel === undefined) {
    return baseElement;
  }

  return (
    <span className="block space-y-2 text-xs text-neutral-50 font-suisse text-center w-fit">
      {baseElement}

      <span className={cn("block", labelsClassName)}>
        {/* continue to take vertical space if empty strings are provided */}
        <p className="whitespace-nowrap">{label === "" ? <br /> : label}</p>
        <p className="whitespace-nowrap font-bold">
          {subLabel === "" ? <br /> : subLabel}
        </p>
      </span>
    </span>
  );
};
